import { createSelector } from 'reselect'
import * as R from 'ramda'

import {
  createSelectorWithPath,
  labelSelectorFunc,
  entitiesSelector
} from 'helpers/ComponentSelectorUtil'
import { formFilterPairs } from 'components/custom/AddPopup/AddPopupUtil'
import { configString } from './constants'

const sourcesSelectorFunc = state => state.sources
const valuesSelectorFunc = state => state.values

const sourcesSelectorHoc = (sourcesSelector) => (
  createSelector(
    entitiesSelector,
    sourcesSelector,
    (entities, refs) => refs.map(({id, type}) => {
      const source = entities[type][id]
      const {
        partType,
        organizationSize,
        organizationWidth,
        density,
      } = source
      return {
        ...source,
        config: configString(partType, organizationSize, organizationWidth, density),
      }
    })
  )
)

const approvedSourcesSelectorHoc = (sourcesSelector) => (
  createSelector(
    sourcesSelector,
    (sources) => sources.filter(source => source.risk === 'Allowed')
  )
)

const riskSourcesSelectorHoc = (sourcesSelector) => (
  createSelector(
    sourcesSelector,
    (sources) => sources.filter(source => source.risk === 'Risk')
  )
)

const valuesSelectorHoc = (valuesSelector) => (
  createSelector(
    valuesSelector,
    (values) => values.map(value => {
      const {
        partType,
        organizationSize,
        organizationWidth,
        density,
      } = value

      return {
        ...value,
        config: configString(partType, organizationSize, organizationWidth, density),
      }
    })
  )
)

const memoryConfigsSelectorHoc = (valuesSelector) => (
  createSelector(
    valuesSelector,
    (values) => {
      // skip selection if values is empty
      if (values.length === 0) return []

      const allParts = Array.from(values)
      let configArr = [[allParts.shift()]]

      allParts.forEach(part => {
        part.config = `${part.partType}, ${part.organizationSize}Mx${part.organizationWidth}, ${part.density}GB`

        // flag if the configuration of the part exists
        let configExists = false

        // find corresponding configuration for the part and update current configuration array
        configArr = configArr.map(partArray => {
          // use the first part of the part array of current configuration
          const config = partArray[0]

          if (part.config === config.config) {
            partArray.push(part)
            configExists = true
          }

          return partArray
        })

        // if configuration does not exist for this part, create a new configuration and
        // push the part in
        if (!configExists) {
          configArr.push([part])
        }
      })

      return configArr
    }
  )
)


const filterConfigSourceSelectorHoc = (isAddPartSelector, configSelector, sourcesSelector) => (
  createSelector(
    sourcesSelector,
    isAddPartSelector,
    configSelector,
    (sources, isAddPart, configs) =>
      !isAddPart ? sources.filter(({config}) => !Object.keys(R.pickBy((val) => !val, configs)).includes(config)) : sources
  )
)

const filterCurConfigSourceSelectorHoc = (isAddPartSelector, curConfigSelector, sourcesSelector) => (
  createSelector(
    sourcesSelector,
    isAddPartSelector,
    curConfigSelector,
    (sources, isAddPart, curConfig) =>
      isAddPart && curConfig.config ? sources.filter(({config}) => curConfig.config === config) : sources
  )
)

// Remove selected values from source
const filterSelectedSourcesSelectorHoc = (valuesSelector, sourcesSelector) => (
  createSelector(
    valuesSelector,
    sourcesSelector,
    (values, sources) => {
      const memIds = values.map(({memoryPartId}) => memoryPartId)
      return sources.filter(({memoryPartId}) => !memIds.includes(memoryPartId))
    }
  )
)

const filterPairsSelectorHoc = (sourcesSelector, curConfigSelector) => (
  createSelector(
    sourcesSelector,
    curConfigSelector,
    (sources, curConfig) => formFilterPairs({ config: 'Configuration', }, sources, curConfig)
  )
)

const popupSelectorFunc = state => state.togglePopup
const configSelectionSelectorFunc = state => state.configSelection
const configSourceSelectorFunc = state => state.configSource
const partTypeSelectorFunc = state => state.partTypeFilter
const densitySelectorFunc = state => state.densityFilter
const orgSizeSelectorFunc = state => state.orgSizeFilter
const orgWidthSelectorFunc = state => state.orgWidthFilter
const channelSelectorFunc = state => state.channelFilter
const configSelectorFunc = state => state.configFilter
const curConfigSelectorFunc = state => state.curConfig
const isAddPartSelectorFunc = state => state.isAddPart

export default (pathName, uiPath) => {
  const withPath = createSelectorWithPath(pathName)
  const withUiPath = createSelectorWithPath(uiPath)

  const labelSelector = withPath(labelSelectorFunc)
  const sourcesSelectorRef = withPath(sourcesSelectorFunc)
  const valuesSelectorRef = withPath(valuesSelectorFunc)

  const sourcesSelector = sourcesSelectorHoc(sourcesSelectorRef)
  const valuesSelector = valuesSelectorHoc(valuesSelectorRef)
  const memoryConfigsSelector = memoryConfigsSelectorHoc(valuesSelector)

  const togglePopupSelector = withUiPath(popupSelectorFunc)
  const configSelectionSelector = withUiPath(configSelectionSelectorFunc)
  const configSourceSelector = withUiPath(configSourceSelectorFunc)
  const partTypeSelector = withUiPath(partTypeSelectorFunc)
  const densitySelector = withUiPath(densitySelectorFunc)
  const orgSizeSelector = withUiPath(orgSizeSelectorFunc)
  const orgWidthSelector = withUiPath(orgWidthSelectorFunc)
  const channelSelector = withUiPath(channelSelectorFunc)
  const configSelector = withUiPath(configSelectorFunc)
  const curConfigSelector = withUiPath(curConfigSelectorFunc)
  const isAddPartSelector = withUiPath(isAddPartSelectorFunc)

  const filteredSourcesSelector =
    R.compose(R.curry(filterSelectedSourcesSelectorHoc)(valuesSelector),
              R.curry(filterConfigSourceSelectorHoc)(isAddPartSelector, configSelector),
              R.curry(filterCurConfigSourceSelectorHoc)(isAddPartSelector, curConfigSelector))(sourcesSelector)
  const approvedSourcesSelector = approvedSourcesSelectorHoc(filteredSourcesSelector)
  const riskSourcesSelector = riskSourcesSelectorHoc(filteredSourcesSelector)
  const filteredApprovedSourcesSelector = filterSelectedSourcesSelectorHoc(valuesSelector, approvedSourcesSelector)
  const filteredRiskSourcesSelector = filterSelectedSourcesSelectorHoc(valuesSelector, riskSourcesSelector)

  const filterPairsSelector = filterPairsSelectorHoc(filteredSourcesSelector, curConfigSelector)
  const approvedFilterPairsSelector = filterPairsSelectorHoc(approvedSourcesSelector, curConfigSelector)
  const riskFilterPairsSelector = filterPairsSelectorHoc(riskSourcesSelector, curConfigSelector)

  return {
    labelSelector,
    valuesSelector,
    memoryConfigsSelector,
    configSelectionSelector,
    configSourceSelector,
    filterPairsSelector,
    approvedFilterPairsSelector,
    riskFilterPairsSelector,
    filteredSourcesSelector,
    approvedSourcesSelector,
    riskSourcesSelector,
    togglePopupSelector,
    partTypeSelector,
    densitySelector,
    orgSizeSelector,
    orgWidthSelector,
    channelSelector,
    configSelector,
    curConfigSelector,
    isAddPartSelector,
  }
}
